import type { AnalyticsBrowser } from "@segment/analytics-next";

import { setTypewriterOptions } from "~/analytics/segment";

type SegmentQueuer = {
  user: () => Promise<undefined>;
  page: (...args: Parameters<AnalyticsBrowser["page"]>) => void;
  track: (...args: Parameters<AnalyticsBrowser["track"]>) => void;
  identify: (...args: Parameters<AnalyticsBrowser["identify"]>) => void;
};

export default defineNuxtPlugin({
  name: "segment",
  parallel: true,
  dependsOn: ["platform"],
  setup: (nuxtApp) => {
    const platformStore = usePlatformStore();
    const featureToggles = useFeatureToggles();

    const writeKey =
      platformStore.tryGetPlatformConfiguration?.tracking.segmentWriteKey;
    const platformId = platformStore.tryGetPlatformConfiguration?.platformId;
    const isTrackingEnabled =
      platformStore.tryGetPlatformConfiguration?.tracking.isEnabled;

    const trackEventQueue: Parameters<AnalyticsBrowser["track"]>[] = [];
    const pageEventQueue: Parameters<AnalyticsBrowser["page"]>[] = [];
    const identifyEventQueue: Parameters<AnalyticsBrowser["identify"]>[] = [];

    // Default behavior for the browser is to save it in a queue
    const browser = ref<AnalyticsBrowser | SegmentQueuer>({
      user: () => {
        // eslint-disable-next-line no-console
        console.warn(
          "You can only get the segment user once the segment instance is loaded",
        );

        return Promise.resolve(undefined);
      },
      page: (...args: Parameters<AnalyticsBrowser["page"]>) => {
        pageEventQueue.push(args);
      },
      track: (...args: Parameters<AnalyticsBrowser["track"]>) => {
        trackEventQueue.push(args);
      },
      identify: (...args: Parameters<AnalyticsBrowser["identify"]>) => {
        identifyEventQueue.push(args);
      },
    });

    // Set the queueing mechanism for typewriter
    setTypewriterOptions({
      analytics: browser.value as AnalyticsBrowser,
    });

    const load = () => {
      if (!isTrackingEnabled || !writeKey || !platformId) {
        // eslint-disable-next-line no-console
        console.warn(
          "Not loading segment because write key or platform id is missing, or because tracking is not enabled for the platform",
        );

        return;
      }

      import("@segment/analytics-next")
        .then(async (importResult) => {
          if (!importResult) {
            // eslint-disable-next-line no-console
            console.warn("Failed to load the segment package");

            return;
          }

          browser.value = new importResult.AnalyticsBrowser().load(
            {
              writeKey,
              cdnURL: "https://a.matchwornshirt.com",
            },
            {
              integrations: {
                "Segment.io": {
                  // POST https://tracking.matchwornshirt.com/v1/t --> proxies to
                  // https://events.eu1.segmentapis.com/
                  apiHost: "t.matchwornshirt.com/v1",
                  protocol: "https", // optional
                },
              },
            },
          );

          if (featureToggles.value.teamContentId)
            browser.value.addDestinationMiddleware(
              "Facebook Pixel",
              facebookDestinationMiddleware(),
            );

          // Overwrite the queueing mechanism for typewriter
          setTypewriterOptions({
            analytics: browser.value as AnalyticsBrowser,
          });

          // segment has its own internal queue, so we don't need to wait for it to be ready
          browser.value.identify({
            platform_id: platformId,
          });

          // Make sure we send over the tracking events in a queue
          await Promise.all(
            identifyEventQueue.map(async (args) => {
              await browser.value.identify(...args);
            }),
          );

          await Promise.all(
            pageEventQueue.map(async (args) => {
              await browser.value.page(...args);
            }),
          );

          await Promise.all(
            trackEventQueue.map(async (args) => {
              await browser.value.track(...args);
            }),
          );
        })
        // make sure we initialize growthbook even if segment fails to load
        .finally(async () => {
          const user = await browser.value.user();
          const publicIpAddress = await tryGetPublicIpAddress();

          await nuxtApp.vueApp.$nuxt.$growthbook?.initialize();
          nuxtApp.vueApp.$nuxt.$growthbook?.identify({
            userId: user?.id(),
            anonymousId: user?.anonymousId(),
            publicIpAddress,
          });
        });
    };

    return {
      provide: {
        segment: {
          browser,
          load,
        },
      },
    };
  },
});
